import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, MessageSquare, FileText, Eye, ArrowRight, Download } from "lucide-react";
import { adminStats, adminCatalogPdfUrl } from "../../lib/adminApi";
import AnalyticsPanel from "./AnalyticsPanel";

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        adminStats().then(setStats).catch(() => setStats({ error: true }));
    }, []);

    const cards = [
        {
            to: "/admin/waitlist",
            icon: Users,
            label: "Waitlist tilmeldinger",
            value: stats?.waitlist_total ?? "—",
            accent: "from-sky-500 to-sky-700",
            testid: "stat-waitlist",
        },
        {
            to: "/admin/contacts",
            icon: MessageSquare,
            label: "Kontakthenvendelser",
            value: stats?.contacts_total ?? "—",
            accent: "from-violet-500 to-violet-700",
            testid: "stat-contacts",
        },
        {
            to: "/admin/posts",
            icon: FileText,
            label: "Indlæg (publicerede)",
            value: stats ? `${stats.posts_published} / ${stats.posts_total}` : "—",
            accent: "from-emerald-500 to-emerald-700",
            testid: "stat-posts",
        },
    ];

    return (
        <div data-testid="admin-dashboard">
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
                Oversigt
            </h1>
            <p className="mt-1 text-slate-600">Velkommen tilbage. Her er status på Cleany pre-launch.</p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
                {cards.map((c) => (
                    <Link
                        key={c.to}
                        to={c.to}
                        data-testid={c.testid}
                        className="group rounded-2xl bg-white border border-slate-200 hover:border-sky-300 hover:-translate-y-1 hover:shadow-lg transition-all p-6"
                    >
                        <div className="flex items-center justify-between">
                            <span className={`h-10 w-10 rounded-lg bg-gradient-to-br ${c.accent} inline-flex items-center justify-center text-white`}>
                                <c.icon className="h-5 w-5" />
                            </span>
                            <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-sky-600 group-hover:translate-x-0.5 transition-all" />
                        </div>
                        <div className="mt-4 text-3xl font-display font-bold text-slate-900">
                            {c.value}
                        </div>
                        <div className="mt-1 text-sm text-slate-500">{c.label}</div>
                    </Link>
                ))}
            </div>

            <div className="mt-10 rounded-2xl bg-white border border-slate-200 p-6">
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-lg font-bold text-slate-900">Hurtige handlinger</h2>
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                    <Link
                        to="/admin/posts/new"
                        data-testid="quick-action-new-post"
                        className="inline-flex items-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold px-4 h-10 text-sm transition-colors"
                    >
                        <FileText className="h-4 w-4" />
                        Opret nyt indlæg
                    </Link>
                    <a
                        href={adminCatalogPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="quick-action-download-catalog"
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 text-slate-700 hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700 px-4 h-10 text-sm font-semibold transition-colors"
                    >
                        <Download className="h-4 w-4" />
                        Hent modulkatalog (PDF)
                        <span className="ml-1 text-[10px] font-bold uppercase tracking-wider rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5">
                            Altid opdateret
                        </span>
                    </a>
                    <Link
                        to="/updates"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 text-slate-700 hover:border-slate-300 px-4 h-10 text-sm font-semibold transition-colors"
                    >
                        <Eye className="h-4 w-4" />
                        Se /updates som besøgende
                    </Link>
                </div>
            </div>

            <div className="mt-10">
                <AnalyticsPanel />
            </div>
        </div>
    );
}
